import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  CheckCircle2,
  FileCheck2,
  Gavel,
  Plane,
  ShieldCheck,
  UserCheck,
  X,
} from 'lucide-react';

interface PolitiqueDeplacementsUSAModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const redFlags = [
  'Entrer comme touriste ou visiteur d’affaires si le mandat réel implique du travail opérationnel sur site.',
  'Improviser une réponse à la frontière ou minimiser l’objectif du déplacement.',
  'Transporter outils, matériel, accès client ou documentation de projet sans dossier de voyage validé.',
  'Laisser un employé voyager sans lettre employeur, mandat cadré et validation du statut applicable.',
  'Retourner à la frontière après un refus ou une note CBP sans stratégie validée par avocat immigration US.',
];

const requiredSteps = [
  {
    title: '1. Qualification du déplacement',
    details:
      'Avant tout achat de billet ou déplacement, le gestionnaire confirme le client, le lieu, la durée, le type d’activités prévues et la valeur livrée aux États-Unis.',
  },
  {
    title: '2. Validation immigration',
    details:
      'Tout mandat USA impliquant installation, support, configuration, intervention technique, formation client ou livraison sur site doit être validé par la direction et/ou l’avocat immigration avant départ.',
  },
  {
    title: '3. Dossier de voyage BZ',
    details:
      'Un dossier PRIsME doit contenir la lettre employeur, l’itinéraire, le mandat approuvé, les contacts client, les activités permises/interdites et la consigne exacte à suivre au point d’entrée.',
  },
  {
    title: '4. Briefing employé obligatoire',
    details:
      'L’employé doit comprendre qu’il répond toujours la vérité, sans invention, sans raccourci et sans contradiction avec les documents transportés.',
  },
  {
    title: '5. Gestion des incidents',
    details:
      'En cas de questionnement, refus, retrait de demande d’admission ou document CBP, l’employé ne retente pas l’entrée et transmet immédiatement les documents à la direction et à l’avocat.',
  },
];

const allowedActivities = [
  'Réunion d’affaires, cadrage, négociation ou gouvernance client.',
  'Observation, collecte d’information ou discussion de besoins sans production opérationnelle sur sol américain.',
  'Formation ou transfert de connaissances seulement si validé comme admissible par le conseil immigration.',
  'Support à distance depuis le Canada lorsque possible, documenté dans le mandat.',
];

const evidenceChecklist = [
  'Lettre employeur signée et cohérente avec le mandat réel.',
  'Ordre de travail ou mandat client approuvé.',
  'Itinéraire, dates, adresse, contacts et raison d’affaires.',
  'Validation du statut/visa applicable ou note de l’avocat.',
  'Briefing employé complété et accusé de réception.',
  'Historique des décisions et exceptions approuvées dans PRIsME.',
];

const raci = [
  { role: 'Responsable', owner: 'Directeur RH / directeur du département', duty: 'Décrire le mandat réel et ouvrir le dossier de validation.' },
  { role: 'Approbateur', owner: 'Direction BZ', duty: 'Autoriser ou bloquer le déplacement selon le risque.' },
  { role: 'Consulté', owner: 'Avocat immigration US', duty: 'Confirmer le statut, visa ou waiver requis selon le contexte.' },
  { role: 'Informés', owner: 'RH / Administration / Employé', duty: 'Conserver les preuves, appliquer la procédure et escalader tout incident.' },
];

export default function PolitiqueDeplacementsUSAModal({ isOpen, onClose }: PolitiqueDeplacementsUSAModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ type: 'spring', damping: 24, stiffness: 220 }}
            className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-3xl border border-sky-300/30 bg-slate-950 text-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sticky top-0 z-10 border-b border-white/10 bg-slate-950/95 px-8 py-6 backdrop-blur">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-sky-300/40 bg-sky-400/10 px-4 py-2 text-sm font-semibold text-sky-100">
                    <Plane className="h-4 w-4" />
                    RAG BZ · Politique préventive · Administration & RH
                  </div>
                  <h2 className="text-3xl font-black tracking-tight md:text-4xl">
                    Déplacements USA — immigration, douanes & activités permises
                  </h2>
                  <p className="mt-3 max-w-4xl text-base leading-relaxed text-slate-300">
                    Cette card documente la politique future de BZ pour éviter les refus d’entrée, les erreurs de
                    déclaration et les risques d’inadmissibilité. Elle ne documente aucun cas individuel; elle sert de
                    référence RAG pour préparer les prochains mandats aux États-Unis.
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-full border border-white/10 bg-white/10 p-3 text-white transition hover:bg-white/20"
                  aria-label="Fermer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="space-y-8 px-8 py-8">
              <section className="grid gap-5 md:grid-cols-3">
                <div className="rounded-2xl border border-red-400/40 bg-red-500/10 p-5">
                  <AlertTriangle className="mb-3 h-8 w-8 text-red-300" />
                  <p className="text-sm uppercase tracking-[0.2em] text-red-200">Principe #1</p>
                  <h3 className="mt-2 text-xl font-bold">La vérité seulement</h3>
                  <p className="mt-2 text-sm leading-relaxed text-red-50/90">
                    Un employé ne doit jamais présenter un voyage comme personnel si l’objectif réel est professionnel.
                  </p>
                </div>
                <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-5">
                  <Gavel className="mb-3 h-8 w-8 text-amber-200" />
                  <p className="text-sm uppercase tracking-[0.2em] text-amber-200">Principe #2</p>
                  <h3 className="mt-2 text-xl font-bold">Validation avant départ</h3>
                  <p className="mt-2 text-sm leading-relaxed text-amber-50/90">
                    Le statut applicable est validé avant le déplacement, surtout pour tout travail technique sur site.
                  </p>
                </div>
                <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/10 p-5">
                  <ShieldCheck className="mb-3 h-8 w-8 text-emerald-200" />
                  <p className="text-sm uppercase tracking-[0.2em] text-emerald-200">Principe #3</p>
                  <h3 className="mt-2 text-xl font-bold">Dossier traçable</h3>
                  <p className="mt-2 text-sm leading-relaxed text-emerald-50/90">
                    Chaque voyage USA doit avoir une justification, des preuves et une décision documentées dans PRIsME.
                  </p>
                </div>
              </section>

              <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
                <div className="mb-5 flex items-center gap-3">
                  <FileCheck2 className="h-7 w-7 text-sky-300" />
                  <h3 className="text-2xl font-bold">Processus obligatoire avant tout déplacement USA</h3>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  {requiredSteps.map((step) => (
                    <div key={step.title} className="rounded-2xl border border-sky-300/20 bg-sky-400/5 p-5">
                      <h4 className="text-lg font-bold text-sky-100">{step.title}</h4>
                      <p className="mt-2 text-sm leading-relaxed text-slate-300">{step.details}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="grid gap-6 lg:grid-cols-2">
                <div className="rounded-3xl border border-red-400/30 bg-red-500/10 p-6">
                  <div className="mb-4 flex items-center gap-3">
                    <AlertTriangle className="h-7 w-7 text-red-300" />
                    <h3 className="text-2xl font-bold">Red flags — à bloquer</h3>
                  </div>
                  <ul className="space-y-3 text-sm leading-relaxed text-red-50/90">
                    {redFlags.map((flag) => (
                      <li key={flag} className="flex gap-3">
                        <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-red-300" />
                        <span>{flag}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-3xl border border-emerald-400/30 bg-emerald-500/10 p-6">
                  <div className="mb-4 flex items-center gap-3">
                    <CheckCircle2 className="h-7 w-7 text-emerald-200" />
                    <h3 className="text-2xl font-bold">Activités généralement admissibles à évaluer</h3>
                  </div>
                  <ul className="space-y-3 text-sm leading-relaxed text-emerald-50/90">
                    {allowedActivities.map((activity) => (
                      <li key={activity} className="flex gap-3">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-200" />
                        <span>{activity}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 rounded-xl border border-emerald-300/30 bg-emerald-300/10 p-3 text-xs leading-relaxed text-emerald-50/90">
                    Note: cette liste n’est pas un avis légal. Toute activité technique sur site doit être validée selon le
                    contexte, le client, la durée, le statut et les règles applicables.
                  </p>
                </div>
              </section>

              <section className="rounded-3xl border border-violet-300/30 bg-violet-500/10 p-6">
                <div className="mb-5 flex items-center gap-3">
                  <UserCheck className="h-7 w-7 text-violet-200" />
                  <h3 className="text-2xl font-bold">RACI de gouvernance</h3>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  {raci.map((item) => (
                    <div key={item.role} className="rounded-2xl border border-violet-200/20 bg-slate-900/60 p-5">
                      <p className="text-sm font-semibold uppercase tracking-[0.18em] text-violet-200">{item.role}</p>
                      <h4 className="mt-2 text-lg font-bold">{item.owner}</h4>
                      <p className="mt-2 text-sm leading-relaxed text-slate-300">{item.duty}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-3xl border border-slate-600 bg-slate-900/80 p-6">
                <div className="mb-5 flex items-center gap-3">
                  <FileCheck2 className="h-7 w-7 text-slate-200" />
                  <h3 className="text-2xl font-bold">Preuves à conserver dans PRIsME</h3>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  {evidenceChecklist.map((item) => (
                    <div key={item} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm text-slate-200">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-sky-300" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
